function createGenerationRequestId() {
  if (globalThis.crypto && typeof globalThis.crypto.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }

  return `chat-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 14)}`;
}

const DEFAULT_ASYNC_GENERATION_TIMEOUT_MS = 330000;
const FIRST_GENERATION_POLL_DELAY_MS = 750;
const ACTIVE_GENERATION_POLL_DELAY_MS = 1500;
const LONG_GENERATION_POLL_DELAY_MS = 2500;
const EXTENDED_GENERATION_POLL_DELAY_MS = 4000;
const HIDDEN_GENERATION_POLL_DELAY_MS = 5000;
const LONG_GENERATION_AFTER_MS = 15000;
const EXTENDED_GENERATION_AFTER_MS = 45000;
const MAX_GENERATION_POLL_JITTER_MS = 250;
const MAX_CONSECUTIVE_STATUS_FAILURES = 4;
const DEFAULT_MAX_ASYNC_GENERATION_POLLS = 240;

function isPendingGenerationResponse(response) {
  return Boolean(
    response?.success !== false &&
      response?.data?.async_text_generation === true &&
      response?.data?.generation_job?.id &&
      response?.data?.generation_job?.token
  );
}

function isRetryableGenerationStatusError(error) {
  const status = Number(error?.status || 0);
  return status === 0 || status === 408 || status === 429 || status >= 500;
}

function getGenerationPollingDelay({
  elapsedMs,
  pollCount,
  pageHidden = false,
  random = Math.random,
}) {
  let delayMs = FIRST_GENERATION_POLL_DELAY_MS;

  if (pollCount > 0) {
    delayMs = ACTIVE_GENERATION_POLL_DELAY_MS;
  }
  if (elapsedMs >= LONG_GENERATION_AFTER_MS) {
    delayMs = LONG_GENERATION_POLL_DELAY_MS;
  }
  if (elapsedMs >= EXTENDED_GENERATION_AFTER_MS) {
    delayMs = EXTENDED_GENERATION_POLL_DELAY_MS;
  }
  if (pageHidden) {
    delayMs = Math.max(delayMs, HIDDEN_GENERATION_POLL_DELAY_MS);
  }

  const randomValue = Math.min(1, Math.max(0, Number(random()) || 0));
  const jitterLimitMs = Math.min(
    MAX_GENERATION_POLL_JITTER_MS,
    Math.floor(delayMs * 0.15)
  );

  return delayMs + Math.floor(randomValue * jitterLimitMs);
}

function waitForPollingDelay(milliseconds) {
  if (
    typeof document === "undefined" ||
    document.visibilityState !== "hidden" ||
    typeof document.addEventListener !== "function"
  ) {
    return new Promise((resolve) => setTimeout(resolve, milliseconds));
  }

  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      resolve();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        finish();
      }
    };
    const timer = setTimeout(finish, milliseconds);
    document.addEventListener("visibilitychange", handleVisibilityChange);
  });
}

async function waitForAsyncTextGeneration(
  initialResponse,
  {
    requestStatus,
    delay = waitForPollingDelay,
    now = () => Date.now(),
    timeoutMs = DEFAULT_ASYNC_GENERATION_TIMEOUT_MS,
    intervalMs = null,
    random = Math.random,
    maxConsecutiveStatusFailures = MAX_CONSECUTIVE_STATUS_FAILURES,
    maxPolls = DEFAULT_MAX_ASYNC_GENERATION_POLLS,
    isPageHidden = () =>
      typeof document !== "undefined" && document.visibilityState === "hidden",
  }
) {
  if (!isPendingGenerationResponse(initialResponse)) {
    return initialResponse;
  }

  const job = initialResponse.data.generation_job;
  const startedAt = now();
  let response = initialResponse;
  let pollCount = 0;
  let consecutiveStatusFailures = 0;

  while (isPendingGenerationResponse(response)) {
    const elapsedMs = now() - startedAt;
    if (pollCount >= Math.max(1, Number(maxPolls) || 1)) {
      const error = new Error(
        "Generation did not finish within the allowed status checks. Please check the conversation again shortly."
      );
      error.status = 408;
      error.statusText = "poll_limit";
      throw error;
    }
    if (elapsedMs >= timeoutMs) {
      const error = new Error(
        "Generation is still processing. Please check the conversation again shortly."
      );
      error.status = 408;
      error.statusText = "timeout";
      throw error;
    }

    const pollDelayMs = Number.isFinite(intervalMs)
      ? Math.max(0, intervalMs)
      : getGenerationPollingDelay({
          elapsedMs,
          pollCount,
          pageHidden: Boolean(isPageHidden()),
          random,
        });

    await delay(Math.min(pollDelayMs, timeoutMs - elapsedMs));

    try {
      response = await requestStatus(job);
      consecutiveStatusFailures = 0;
    } catch (error) {
      consecutiveStatusFailures++;
      if (
        !isRetryableGenerationStatusError(error) ||
        consecutiveStatusFailures > maxConsecutiveStatusFailures
      ) {
        throw error;
      }
    }

    pollCount++;
  }

  return response;
}

module.exports = {
  DEFAULT_ASYNC_GENERATION_TIMEOUT_MS,
  DEFAULT_MAX_ASYNC_GENERATION_POLLS,
  createGenerationRequestId,
  getGenerationPollingDelay,
  isPendingGenerationResponse,
  isRetryableGenerationStatusError,
  waitForPollingDelay,
  waitForAsyncTextGeneration,
};
